/* Progressive enhancement: Shopify's native forms remain the no-JavaScript path. */
(() => {
  'use strict';
  const strings = JSON.parse(document.getElementById('FloqStrings')?.textContent || '{}');
  document.addEventListener('click', (event) => {
    document.querySelectorAll('.locale-picker[open]').forEach((picker) => {
      if (!picker.contains(event.target)) picker.open = false;
    });
  });
  document.addEventListener('keydown', (event) => {
    const picker = event.target.closest?.('.locale-picker[open]');
    if (event.key === 'Escape' && picker) {
      picker.open = false;
      picker.querySelector('summary').focus();
    }
  });
  function setupCardGalleries(root = document) {
    root.querySelectorAll('.card-gallery-dots').forEach((dots) => {
      if (dots.dataset.initialized) return;
      dots.dataset.initialized = 'true';
      dots.hidden = false;
      const image = dots.closest('.product-card').querySelector('.product-card-image img');
      const buttons = [...dots.querySelectorAll('[data-card-image]')];
      buttons.forEach((button, index) => {
        button.addEventListener('click', () => {
          image.srcset = button.dataset.cardSrcset;
          image.src = button.dataset.cardImage;
          image.alt = button.dataset.cardAlt;
          buttons.forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
        });
        button.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
          event.preventDefault();
          const next =
            buttons[(index + (event.key === 'ArrowRight' ? 1 : -1) + buttons.length) % buttons.length];
          next.focus();
          next.click();
        });
      });
    });
  }
  setupCardGalleries();
  document.addEventListener('shopify:section:load', (event) => setupCardGalleries(event.target));
  function setupMediaFallbacks(root = document) {
    root.querySelectorAll('[data-hero-film], [data-product-film]').forEach((video) => {
      if (video.dataset.fallbackReady) return;
      video.dataset.fallbackReady = 'true';
      video.controls = false;
      video.defaultMuted = true;
      video.muted = true;
      // The standalone image remains visible until the browser has a playable frame.
      video.addEventListener('playing', () => video.classList.add('has-frame'));
      const failed = () => {
        video.dataset.failed = 'true';
        video.classList.remove('has-frame');
        const button =
          video.parentElement.querySelector('[data-hero-playback]') ||
          video.closest('[data-media-gallery]')?.querySelector('[data-media-playback]');
        if (button) button.hidden = true;
      };
      video.addEventListener('error', failed);
      video.querySelectorAll('source').forEach(source => source.addEventListener('error', failed));
      ['error', 'emptied'].forEach((event) =>
        video.addEventListener(event, () => video.classList.remove('has-frame')),
      );
    });
  }
  setupMediaFallbacks();
  document.addEventListener('shopify:section:load', (event) => setupMediaFallbacks(event.target));
  document.addEventListener('click', (event) => {
    document.querySelectorAll('.store-menu[open]').forEach((menu) => {
      if (!menu.contains(event.target) || event.target.closest('nav a')) menu.open = false;
    });
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const menu = event.target.closest('.store-menu[open]');
    if (menu && !event.target.closest('.locale-picker')) {
      menu.open = false;
      menu.querySelector('summary').focus();
    }
  });
  function setupBenefits(root = document) {
    root.querySelectorAll('[data-benefits-track]').forEach(track => {
      if (track.dataset.initialized) return;
      track.dataset.initialized = 'true';
      const controls = track.parentElement.querySelector('[data-benefits-controls]');
      const items = [...track.children];
      const prev = controls.querySelector('[data-benefits-prev]');
      const next = controls.querySelector('[data-benefits-next]');
      const update = () => {
        const overflow = track.scrollWidth > track.clientWidth + 2;
        controls.hidden = !overflow;
        track.tabIndex = overflow ? 0 : -1;
        const step = items[1].offsetLeft - items[0].offsetLeft || 1;
        const index = Math.round(track.scrollLeft / step);
        const visible = Math.max(1, Math.floor((track.clientWidth + 1) / (items[0].offsetWidth || 1))); 
        prev.disabled = track.scrollLeft < 2;
        next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
        controls.querySelector('[data-benefits-status]').textContent = visible > 1 ? `${index + 1}-${Math.min(index + visible, items.length)} / ${items.length}` : `${index + 1} / ${items.length}`;
      };
      const move = (direction) => track.scrollBy({ left: direction * (items[1].offsetLeft - items[0].offsetLeft), behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      prev.addEventListener('click', () => move(-1));
      next.addEventListener('click', () => move(1));
      track.addEventListener('keydown', event => {
        if (event.target !== track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault(); move(event.key === 'ArrowRight' ? 1 : -1);
      });
      track.addEventListener('scroll', update, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(update).observe(track);
      update();
    });
  }
  setupBenefits();
  document.addEventListener('shopify:section:load', event => setupBenefits(event.target));
  function setupHeroFilms(root = document) {
    root.querySelectorAll('[data-hero-film]').forEach((video) => {
      if (video.dataset.initialized) return;
      video.dataset.initialized = 'true';
      const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      video.controls = false;
      video.muted = true;
      const button = video.parentElement.querySelector('[data-hero-playback]');
      const pauseLabel = button?.getAttribute('aria-label');
      video.addEventListener('waiting', () => video.classList.remove('has-frame'));
      video.addEventListener('stalled', () => { if (video.readyState < 3) video.classList.remove('has-frame'); });
      const syncButton = () => {
        if (!button) return;
        button.textContent = video.paused ? '▷' : 'Ⅱ';
        button.setAttribute('aria-label', video.paused ? button.dataset.playLabel : pauseLabel);
      };
      if (button) {
        button.hidden = false;
        button.addEventListener('click', () => {
          video.dataset.userPaused = String(!video.paused);
          if (video.paused) video.play().catch(syncButton);
          else video.pause();
        });
        video.addEventListener('play', syncButton);
        video.addEventListener('pause', syncButton);
        syncButton();
      }
      const sync = () => {
        if (
          motion.matches ||
          navigator.connection?.saveData ||
          document.hidden ||
          video.dataset.userPaused === 'true'
        )
          video.pause();
        else video.play().catch(syncButton);
      };
      motion.addEventListener('change', sync);
      document.addEventListener('visibilitychange', sync);
      sync();
    });
  }
  setupHeroFilms();
  document.addEventListener('shopify:section:load', (event) => setupHeroFilms(event.target));
  function setupProductGalleries(root = document) {
    root.querySelectorAll('[data-media-gallery]').forEach((gallery) => {
      if (gallery.dataset.initialized) return;
      gallery.dataset.initialized = 'true';
      const slides = [...gallery.querySelectorAll('[data-media-slide]')];
      if (!slides.length) return;
      const stage = gallery.querySelector('.media-stage');
      const thumbs = gallery.querySelector('[data-media-thumbnails]');
      const status = gallery.querySelector('[data-media-status]');
      const playback = gallery.querySelector('[data-media-playback]');
      let current = 0;
      const buttons = slides.map((slide, index) => {
        const video = slide.querySelector('video');
        const original = slide.querySelector('img');
        const button = document.createElement('button');
        button.type = 'button';
        button.setAttribute('aria-controls', stage.id);
        button.setAttribute(
          'aria-label',
          `${video ? gallery.dataset.videoLabel : original?.alt || gallery.getAttribute('aria-label')} - ${index + 1} / ${slides.length}`,
        );
        const image = original ? original.cloneNode() : document.createElement('img');
        if (video) {
          image.removeAttribute('srcset');
          image.src = video.poster;
        }
        image.removeAttribute('id');
        image.removeAttribute('class');
        image.removeAttribute('fetchpriority');
        image.alt = '';
        image.loading = 'lazy';
        image.sizes = '80px';
        button.append(image);
        if (video) {
          const badge = document.createElement('span');
          badge.textContent = '▷';
          badge.className = 'media-video-badge';
          badge.setAttribute('aria-hidden', 'true');
          button.append(badge);
        }
        button.addEventListener('click', () => select(index));
        thumbs.append(button);
        return button;
      });
      const syncPlayback = () => {
        if (!playback) return;
        const video = slides[current].querySelector('video');
        playback.hidden = !video || Boolean(video.error) || video.dataset.failed === 'true';
        if (!video) return;
        playback.setAttribute(
          'aria-label',
          video.paused ? gallery.dataset.playLabel : gallery.dataset.pauseLabel,
        );
        playback.firstElementChild.textContent = video.paused ? '▷' : 'Ⅱ';
      };
      const select = (index) => {
        current = (index + slides.length) % slides.length;
        slides.forEach((slide, i) => {
          slide.hidden = i !== current;
          slide.querySelectorAll('video').forEach((video) => {
            if (slide.hidden) video.pause();
            video.dispatchEvent(new Event('floq:mediachange'));
          });
        });
        buttons.forEach((button, i) => button.setAttribute('aria-pressed', String(i === current)));
        status.textContent = `${current + 1} / ${slides.length}`;
        const button = buttons[current];
        if (
          button.offsetLeft < thumbs.scrollLeft ||
          button.offsetLeft + button.offsetWidth > thumbs.scrollLeft + thumbs.clientWidth
        ) {
          thumbs.scrollLeft = button.offsetLeft - thumbs.offsetLeft;
        }
        syncPlayback();
      };
      gallery.querySelector('[data-media-prev]').addEventListener('click', () => select(current - 1));
      gallery.querySelector('[data-media-next]').addEventListener('click', () => select(current + 1));
      gallery.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        select(
          event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? slides.length - 1
              : current + (event.key === 'ArrowRight' ? 1 : -1),
        );
        if (thumbs.contains(event.target)) buttons[current].focus({ preventScroll: true });
      });
      let start;
      stage.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' || event.target.closest('button')) return;
        start = { x: event.clientX, y: event.clientY };
      });
      stage.addEventListener('pointerup', (event) => {
        if (!start) return;
        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;
        start = null;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.5) select(current + (dx < 0 ? 1 : -1));
      });
      stage.addEventListener('pointercancel', () => {
        start = null;
      });
      slides.forEach((slide) =>
        slide.querySelectorAll('video').forEach((video) => {
          video.addEventListener('play', syncPlayback);
          video.addEventListener('pause', syncPlayback);
        }),
      );
      playback?.addEventListener('click', () => {
        const video = slides[current].querySelector('video');
        if (!video) return;
        video.dataset.userPaused = String(!video.paused);
        if (video.paused) video.play().catch(syncPlayback);
        else video.pause();
        syncPlayback();
      });
      gallery.querySelector('[data-gallery-controls]').hidden = slides.length < 2;
      thumbs.hidden = slides.length < 2;
      select(0);
    });
  }
  setupProductGalleries();
  document.addEventListener('shopify:section:load', (event) => setupProductGalleries(event.target));
  function setupProductFilms(root = document) {
    root.querySelectorAll('[data-product-film]').forEach((video) => {
      if (video.dataset.initialized) return;
      video.dataset.initialized = 'true';
      video.muted = true;
      const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      let visible = false;
      const cardButton = video.hasAttribute('data-film-card') ? video.closest('.product-card')?.querySelector('[data-card-playback]') : null;
      if (cardButton) {
        cardButton.hidden = false;
        const updateButton = () => {
          cardButton.textContent = video.paused ? '▷' : 'Ⅱ';
          cardButton.setAttribute('aria-label', video.paused ? cardButton.dataset.playLabel : cardButton.dataset.pauseLabel);
        };
        cardButton.addEventListener('click', () => {
          video.dataset.userPaused = String(!video.paused);
          if (video.paused) { video.muted = true; video.play().catch(updateButton); }
          else video.pause();
        });
        video.addEventListener('play', updateButton); video.addEventListener('pause', updateButton);
        updateButton();
      }
      const sync = () => {
        if (
          !visible ||
          document.hidden ||
          video.closest('[data-media-slide]')?.hidden ||
          video.dataset.userPaused === 'true' ||
          motion.matches ||
          navigator.connection?.saveData
        ) {
          video.pause();
        } else {
          video.play().catch(() => {});
        }
      };
      video.addEventListener('floq:mediachange', sync);
      motion.addEventListener('change', sync);
      document.addEventListener('visibilitychange', sync);
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(
          (entries) => {
            visible = entries[0].isIntersecting;
            sync();
          },
          { threshold: 0.15 },
        );
        observer.observe(video);
        document.addEventListener('shopify:section:unload', (event) => {
          if (!event.target.contains(video)) return;
          observer.disconnect();
          motion.removeEventListener('change', sync);
          document.removeEventListener('visibilitychange', sync);
        });
      }
    });
  }
  setupProductFilms();
  document.addEventListener('shopify:section:load', (event) => setupProductFilms(event.target));
  const body = document.body;
  const route = {
    cart: body.dataset.cartUrl,
    add: body.dataset.cartAddUrl,
    change: body.dataset.cartChangeUrl,
  };
  const drawer = document.getElementById('CartDrawer');
  let returnFocus = null;
  let cartBusy = false;
  const announce = (message) => {
    const node = document.getElementById('shop-status');
    if (node) node.textContent = message;
  };
  const setError = (node, message) => {
    if (node) node.textContent = message;
  };
  const openCart = (trigger) => {
    if (!drawer?.showModal) {
      location.assign(route.cart);
      return;
    }
    returnFocus = trigger || document.activeElement;
    if (!drawer.open) drawer.showModal();
  };
  const closeCart = () => {
    drawer?.close();
    if (returnFocus?.isConnected) returnFocus.focus();
  };
  function updateDrawer(html) {
    if (typeof html !== 'string') return false;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const content = parsed.querySelector('[data-cart-content]');
    if (!content || !drawer) return false;
    drawer.replaceChildren(document.importNode(content, true));
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = '(' + content.dataset.itemCount + ')';
    });
    return true;
  }
  async function cartRequest(url, payload) {
    let response;
    try {
      response = await fetch(url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...payload, sections: ['cart-drawer'], sections_url: route.cart }),
      });
    } catch {
      throw new Error(
        strings.unconfirmed || 'We could not confirm the update. Check your bag before trying again.',
      );
    }
    let data;
    try {
      data = await response.json();
    } catch {
      throw new Error(
        strings.unconfirmed || 'We could not confirm the update. Check your bag before trying again.',
      );
    }
    if (!response.ok)
      throw new Error(
        typeof data.description === 'string'
          ? data.description
          : typeof data.message === 'string'
            ? data.message
            : strings.unavailable || 'This quantity is unavailable. Please try a smaller quantity.',
      );
    return data;
  }
  async function changeLine(key, quantity) {
    if (cartBusy) return;
    if (!Number.isInteger(quantity) || quantity < 0)
      return setError(
        drawer?.querySelector('[data-cart-error]'),
        strings.nonnegative || 'Enter a whole number of items, zero or more.',
      );
    cartBusy = true;
    const focusId = document.activeElement?.id;
    const controls = drawer?.querySelectorAll('button,input') || [];
    controls.forEach((el) => (el.disabled = true));
    try {
      const data = await cartRequest(route.change, { id: key, quantity });
      if (!updateDrawer(data.sections?.['cart-drawer'])) {
        location.assign(route.cart);
        return;
      }
      announce(strings.updated || 'Your bag has been updated.');
      const focus = focusId ? document.getElementById(focusId) : null;
      (focus || drawer?.querySelector('[data-close-cart]'))?.focus();
    } catch (error) {
      setError(drawer?.querySelector('[data-cart-error]'), error.message);
    } finally {
      cartBusy = false;
      controls.forEach((el) => (el.disabled = false));
    }
  }
  document.addEventListener('click', (event) => {
    const open = event.target.closest('[data-open-cart]');
    if (open && drawer?.showModal) {
      event.preventDefault();
      document.querySelector('.mobile-nav')?.removeAttribute('open');
      openCart(open);
    }
    if (event.target.closest('[data-close-cart]')) closeCart();
    const remove = event.target.closest('[data-remove-item]');
    if (remove && drawer?.contains(remove)) {
      event.preventDefault();
      changeLine(remove.dataset.removeItem, 0);
    }
    const imageButton = event.target.closest('[data-gallery-image]');
    if (imageButton) {
      const gallery = imageButton.closest('.product-gallery');
      const main = gallery.querySelector('.product-main-image');
      if (main) {
        main.src = imageButton.dataset.galleryImage;
        main.removeAttribute('srcset');
        main.alt = imageButton.dataset.imageAlt;
        gallery
          .querySelectorAll('[data-gallery-image]')
          .forEach((b) => b.setAttribute('aria-pressed', String(b === imageButton)));
      }
    }
    if (event.target.closest('.mobile-nav nav a'))
      document.querySelector('.mobile-nav')?.removeAttribute('open');
  });
  drawer?.addEventListener('click', (event) => {
    if (event.target === drawer) {
      const bounds = drawer.getBoundingClientRect();
      if (
        event.clientX < bounds.left ||
        event.clientX > bounds.right ||
        event.clientY < bounds.top ||
        event.clientY > bounds.bottom
      )
        closeCart();
    }
  });
  drawer?.addEventListener('cancel', () => {
    if (returnFocus?.isConnected) returnFocus.focus();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') document.querySelector('.mobile-nav')?.removeAttribute('open');
  });
  document.addEventListener('change', (event) => {
    if (!event.target.matches('[data-variant-select]')) return;
    const select = event.target;
    const option = select.selectedOptions[0];
    const section = select.closest('[data-product-section]');
    const button = section.querySelector('[data-add-button]');
    section.querySelector('[data-product-price]').textContent = option.dataset.price;
    const compare = section.querySelector('[data-compare-price]');
    compare.textContent = option.dataset.comparePrice;
    compare.hidden = !option.dataset.comparePrice;
    button.disabled = option.dataset.available !== 'true';
    button.textContent = button.disabled ? strings.sold_out || 'Sold out' : strings.add || 'Add to bag';
    const accelerated = section.querySelector('[data-accelerated-checkout]');
    if (accelerated) accelerated.hidden = button.disabled;
    setError(section.querySelector('[data-product-error]'), '');
    const url = new URL(location.href);
    url.searchParams.set('variant', select.value);
    history.replaceState(null, '', url);
    if (option.dataset.image) {
      const image = section.querySelector('.product-main-image');
      if (image) {
        image.src = option.dataset.image;
        image.removeAttribute('srcset');
      }
    }
  });
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (form.matches('[data-cart-line-form]')) {
      event.preventDefault();
      const data = new FormData(form);
      changeLine(data.get('id'), Number(data.get('quantity')));
      return;
    }
    if (!form.matches('.product-form') || !drawer?.showModal) return;
    if (event.submitter && !event.submitter.matches('[data-add-button]')) return;
    event.preventDefault();
    if (form.dataset.submitting === 'true' || cartBusy) return;
    const data = new FormData(form);
    const quantity = Number(data.get('quantity'));
    const errorNode = form.querySelector('[data-product-error]');
    if (!Number.isInteger(quantity) || quantity < 1) {
      setError(errorNode, strings.positive || 'Enter a whole number of items, one or more.');
      return;
    }
    const button = form.querySelector('[data-add-button]');
    const select = form.querySelector('[data-variant-select]');
    if (select.selectedOptions[0].dataset.available !== 'true') return;
    form.dataset.submitting = 'true';
    cartBusy = true;
    button.disabled = true;
    select.disabled = true;
    button.textContent = strings.adding || 'Adding…';
    setError(errorNode, '');
    try {
      const properties = {};
      for (const [key, value] of data) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match) properties[match[1]] = value;
      }
      const payload = {
        items: [
          {
            id: data.get('id'),
            quantity,
            properties,
            ...(data.get('selling_plan') ? { selling_plan: data.get('selling_plan') } : {}),
          },
        ],
      };
      const result = await cartRequest(route.add, payload);
      if (!updateDrawer(result.sections?.['cart-drawer'])) {
        location.assign(route.cart);
        return;
      }
      announce(strings.added || 'Added to your bag.');
      openCart(button);
    } catch (error) {
      setError(errorNode, error.message);
    } finally {
      delete form.dataset.submitting;
      cartBusy = false;
      select.disabled = false;
      button.disabled = select.selectedOptions[0].dataset.available !== 'true';
      button.textContent = button.disabled ? strings.sold_out || 'Sold out' : strings.add || 'Add to bag';
      const accelerated = form.querySelector('[data-accelerated-checkout]');
      if (accelerated) accelerated.hidden = button.disabled;
    }
  });
})();

/* Footer links collapse only on smaller screens, with full keyboard access. */
(() => {
  const small = window.matchMedia('(max-width: 1100px)');
  function setup(root = document) {
    root.querySelectorAll('.footer-column').forEach((column, index) => {
      if (column.dataset.disclosure) return;
      column.dataset.disclosure = 'true';
      const title = column.querySelector('h2');
      const button = document.createElement('button');
      button.type = 'button'; button.className = 'footer-toggle';
      button.textContent = title.textContent; button.setAttribute('aria-label', title.textContent);
      const links = document.createElement('div');
      links.id = `footer-links-${index}-${column.closest('[id]')?.id || 'main'}`;
      button.setAttribute('aria-controls', links.id);
      while (title.nextSibling) links.append(title.nextSibling);
      title.replaceChildren(button); column.append(links);
      function sync() { links.hidden = small.matches; button.setAttribute('aria-expanded', String(!links.hidden)); button.disabled = !small.matches; }
      button.addEventListener('click', () => { links.hidden = !links.hidden; button.setAttribute('aria-expanded', String(!links.hidden)); });
      small.addEventListener('change', sync); sync();
    });
  }
  setup(); document.addEventListener('shopify:section:load', event => setup(event.target));
})();

/* Read by topic on touch screens; every original paragraph stays available. */
(() => {
  const small = window.matchMedia('(max-width: 1100px)');
  const strings = JSON.parse(document.getElementById('FloqStrings')?.textContent || '{}');
  function setup(root = document) {
    root.querySelectorAll('.shopify-policy__body').forEach(body => {
      if (body.dataset.grouped) return;
      body.dataset.grouped = 'true';
      body.querySelectorAll('h2').forEach(heading => {
        const section = document.createElement('section');
        heading.before(section); section.append(heading);
        while (section.nextSibling && !section.nextSibling.matches?.('h1,h2,section')) section.append(section.nextSibling);
        section.classList.add('reading-section');
      });
      const first = body.querySelector('.reading-section');
      if (first) {
        const introNodes=[];let node=first.parentElement.firstChild;
        while(node && node!==first){introNodes.push(node);node=node.nextSibling;}
        if(introNodes.some(item=>item.textContent.trim())) {
          const intro=document.createElement('section');intro.className='reading-section';
          const heading=document.createElement('h2');heading.textContent=strings.policy_intro || 'About this policy';intro.append(heading);
          first.before(intro);introNodes.forEach(item=>intro.append(item));
        }
      }
    });
    root.querySelectorAll('.universe-body,.shopify-policy__body').forEach((body, bodyIndex) => {
      const topics = [...body.querySelectorAll(':scope > section, .reading-section')].filter(section => section.querySelector(':scope > h2') && !section.classList.contains('policy-directory'));
      if (!topics.length || body.dataset.readingReady) return;
      body.dataset.readingReady = 'true';
      const sections = topics.map((section,index) => {
        section.classList.add('reading-section');
        const heading = section.querySelector(':scope > h2');
        const button = document.createElement('button');button.type='button';button.className='reading-toggle';
        button.textContent = heading.textContent;button.setAttribute('aria-label',heading.textContent);
        const panel = document.createElement('div');panel.className='reading-panel';panel.id=`reading-${bodyIndex}-${index}`;
        while (heading.nextSibling) panel.append(heading.nextSibling);
        button.setAttribute('aria-controls',panel.id);heading.replaceChildren(button);section.append(panel);
        function setOpen(open) { panel.hidden=!open;button.setAttribute('aria-expanded',String(open)); }
        button.addEventListener('click',()=>setOpen(panel.hidden));
        const sync=()=>{button.disabled=!small.matches;setOpen(!small.matches);};small.addEventListener('change',sync);sync();
        return {section,setOpen};
      });
      const all=document.createElement('button');all.type='button';all.className='read-all';all.textContent=strings.read_all || 'Read all';
      all.addEventListener('click',()=>sections.forEach(item=>item.setOpen(true)));body.prepend(all);
      const revealHash=()=>{
        const id=decodeURIComponent(location.hash.slice(1));if(!id)return;
        const target=document.getElementById(id);
        sections.filter(item=>item.section===target || item.section.contains(target)).forEach(item=>item.setOpen(true));
      };
      window.addEventListener('hashchange',revealHash);revealHash();
      window.addEventListener('beforeprint',()=>sections.forEach(item=>item.setOpen(true)));
    });
  }
  setup();document.addEventListener('shopify:section:load',event=>setup(event.target));
})();

// The moving brand line can be paused without affecting video playback.
document.querySelectorAll('[data-ticker-toggle]').forEach((button) => {
  button.addEventListener('click', () => {
    const paused = button.closest('.brand-ticker').classList.toggle('is-paused');
    button.setAttribute('aria-pressed', String(paused));
  });
});

// Play the launch once in view, with no visible playback controls.
(() => {
  function setup(root = document) {
    root.querySelectorAll('video').forEach(video => { video.controls = false; });
    root.querySelectorAll('[data-collection-launch]').forEach(block => {
      if (block.dataset.ready) return;
      block.dataset.ready = 'true';
      const video = block.querySelector('video');
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      let completed = false;
      video.muted = true;
      video.addEventListener('ended', () => { completed = true; });
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (!entry.isIntersecting) { video.pause(); return; }
            if (!completed && !reduced.matches && !navigator.connection?.saveData && !document.hidden) video.play().catch(() => {});
          });
        }, {threshold:0.35});
        observer.observe(video);
      }
      document.addEventListener('visibilitychange', () => { if (document.hidden) video.pause(); });
      reduced.addEventListener('change', () => { if (reduced.matches) video.pause(); });
    });
  }
  setup(); document.addEventListener('shopify:section:load', event => setup(event.target));
})();

(() => {
  const panel = document.getElementById('FloqSearch');
  if (!panel || !panel.showModal) return;
  const input = panel.querySelector('input[type="search"]');
  const cards = [...panel.querySelectorAll('[data-search-card]')];
  const clear = panel.querySelector('[data-clear-search]');
  const matcher = window.FloqSearch;
  if (!matcher) return;
  let opener;
  const filter = () => {
    const words = matcher.tokenize(input.value);
    let count = 0;
    cards.forEach(card => {
      card.hidden = !matcher.matches(input.value, card.dataset.searchTerms);
      if (!card.hidden) count++;
    });
    clear.hidden = !input.value;
    panel.querySelector('[data-search-empty]').hidden = count > 0;
    panel.querySelector('[data-search-count]').textContent = words.length ? String(count) : '';
  };
  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-open-search]');
    if (!trigger || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    opener = trigger;
    document.querySelectorAll('.store-menu[open]').forEach(menu => menu.open = false);
    panel.showModal();
    filter();
    input.focus();
  });
  input.addEventListener('input', filter);
  panel.querySelector('form').addEventListener('submit', event => {
    event.preventDefault();
    filter();
    const first = cards.find(card => !card.hidden);
    if (first) first.focus();
  });
  clear.addEventListener('click', () => { input.value = ''; filter(); input.focus(); });
  panel.querySelector('[data-close-search]').addEventListener('click', () => panel.close());
  panel.addEventListener('click', event => { if (event.target === panel && event.clientY > panel.getBoundingClientRect().bottom) panel.close(); });
  panel.addEventListener('close', () => opener?.focus());
})();
